import React, { useState, useEffect, useMemo, useRef } from "react";
import { Col, Dropdown, DropdownMenu, DropdownToggle, Row } from "reactstrap";
import { Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { createSelector } from "reselect";

import SimpleBar from "simplebar-react";
import { DateTime } from "luxon";
import moment from "moment-timezone";

import dummyImg from "../../assets/images/users/user-dummy-img.jpg";

import {
  getVFeesBalance as onGetVFeesBalance,
  getVFeesCompany as onGetVFeesCompany,
  addNewVFee as onAddNewVFee,
  deleteVFee as onDeleteVFee,
  getUsers as onGetUsers,
  getOrdersReceivers as onGetOrdersReceivers,
  getOrdersSenders as onGetOrdersSenders,
} from "../../slices/thunks";

const AlertOfExpire = () => {
  const dispatch = useDispatch();

  const [vfeesBalanceList, setVFeesBalanceList] = useState([]);
  const [dateRange, setDateRange] = useState([]);
  const [total, setTotal] = useState(0);
  const dateRangeRef = useRef([]);

  const handleValidDate = (time) => {
    if (!time) return "";
    return moment(time).tz("Asia/Ulaanbaatar").format("YYYY-MM-DD");
  };

  const handleValidDateTime = (time) => {
    if (!time) return "";
    const parsed = new Date(time);
    if (isNaN(parsed.getTime())) return "";
    return moment.tz(parsed, "Asia/Ulaanbaatar").format("YYYY-MM-DD HH:mm:ss");
  };

  const selectOrdersLayoutState = (state) => state.Order;
  const ordersData = createSelector(selectOrdersLayoutState, (state) => ({
    ordersReceivers: state.ordersReceivers,
    isOrdersReceiversSuccess: state.isOrdersReceiversSuccess,

    ordersSenders: state.ordersSenders,
    isOrdersSendersSuccess: state.isOrdersSendersSuccess,
  }));

  const {
    ordersReceivers,
    isOrdersReceiversSuccess,
    ordersSenders,
    isOrdersSendersSuccess,
  } = useSelector(ordersData);

  const selectVFeesLayoutState = (state) => state.VFee;
  const vfeesData = createSelector(selectVFeesLayoutState, (state) => ({
    vfeesBalance: state.vfeesBalance,
    isVFeesBalanceSuccess: state.isVFeesBalanceSuccess,
    error: state.error,
    isLoading: state.isLoading,
    vfeesCompany: state.vfeesCompany,
    isVFeesCompanySuccess: state.isVFeesCompanySuccess,
    isVFeesCompanyLoading: state.isVFeesCompanyLoading,
  }));

  const {
    vfeesBalance,
    isVFeesBalanceSuccess,
    vfeesCompany,
    isVFeesCompanySuccess,
    error,
    isLoading,
    isVFeesCompanyLoading,
  } = useSelector(vfeesData);

  const selectLayoutState = (state) => state.Crm;
  const crmuserData = createSelector(selectLayoutState, (state) => ({
    crmusers: state.crmusers,
    isUserSuccess: state.isUserSuccess,
    error: state.error,
  }));

  const { crmusers, isUserSuccess } = useSelector(crmuserData);

  useEffect(() => {
    if (crmusers && !crmusers.length) {
      dispatch(onGetUsers());
    }
  }, [dispatch, crmusers]);

  useEffect(() => {
    if (!Array.isArray(ordersReceivers) || ordersReceivers.length === 0) {
      dispatch(onGetOrdersReceivers());
    }
  }, [dispatch, ordersReceivers]);

  useEffect(() => {
    if (!Array.isArray(ordersSenders) || ordersSenders.length === 0) {
      dispatch(onGetOrdersSenders());
    }
  }, [dispatch, ordersSenders]);

  useEffect(() => {
    if (!Array.isArray(ordersReceivers) || !Array.isArray(ordersSenders))
      return;

    const receivers = ordersReceivers
      .filter((r) => r.receiver_is_contracted)
      .map((r) => ({
        company_name: r.receiver_name,
        phone: r.receiver_contacts?.[0]?.phone || "",
        contract_enddate: r.receiver_contract_enddate,
        image_url: dummyImg,
      }));

    const senders = ordersSenders
      .filter((s) => s.sender_is_contracted)
      .map((s) => ({
        company_name: s.sender_name,
        phone: s.sender_phone || "",
        contract_enddate: s.sender_contract_enddate,
        image_url: dummyImg,
      }));

    const combinedArray = [...receivers, ...senders];

    const now = moment();

    const filtered = combinedArray
      .map((item) => {
        if (!item.contract_enddate) return null;
        const daysToExpire = moment(item.contract_enddate).diff(now, "days");
        return { ...item, daysToExpire };
      })
      .filter((item) => item && item.daysToExpire <= 7)
      .sort((a, b) => a.daysToExpire - b.daysToExpire);

    setVFeesBalanceList(filtered);
  }, [ordersReceivers, ordersSenders]);

  const [isCartDropdown, setIsCartDropdown] = useState(false);

  const toggleCartDropdown = () => {
    setIsCartDropdown(!isCartDropdown);
  };

  return (
    <React.Fragment>
      <Dropdown
        isOpen={isCartDropdown}
        toggle={toggleCartDropdown}
        className="topbar-head-dropdown ms-1 header-item"
      >
        <DropdownToggle
          type="button"
          tag="button"
          className="btn btn-icon btn-topbar btn-ghost-secondary rounded-circle"
        >
          <i className="ri-community-line fs-22"></i>
          <span className="position-absolute cartitem-badge topbar-badge fs-10 translate-middle badge rounded-pill bg-warning">
            {vfeesBalanceList.length}
            <span className="visually-hidden">unread messages</span>
          </span>
        </DropdownToggle>
        <DropdownMenu
          className="dropdown-menu-xl dropdown-menu-end p-0 dropdown-menu-cart"
          aria-labelledby="page-header-cart-dropdown"
          style={{ borderRadius: "0 0 14px 14px", overflow: "hidden", boxShadow: "0 8px 32px rgba(0,0,0,0.13)" }}
        >
          <div style={{ background: "linear-gradient(135deg, #f7b84b 0%, #d68910 100%)", padding: "16px 18px 14px" }}>
            <div className="d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center gap-2">
                <div style={{ width: 32, height: 32, borderRadius: "50%", background: "rgba(255,255,255,.2)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <i className="ri-time-line" style={{ color: "#fff", fontSize: 16 }}></i>
                </div>
                <div>
                  <div style={{ color: "#fff", fontWeight: 700, fontSize: 14 }}>Хугацаа дуусах гэж буй</div>
                  <div style={{ color: "rgba(255,255,255,.85)", fontSize: 11 }}>{vfeesBalanceList.length} компани</div>
                </div>
              </div>
              <div style={{ width: 36, height: 36, borderRadius: "50%", background: "rgba(255,255,255,.2)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 800, fontSize: 15 }}>
                {vfeesBalanceList.length}
              </div>
            </div>
          </div>
          <SimpleBar style={{ maxHeight: "300px" }}>
            <div className="p-2">
              {vfeesBalanceList.map((item, key) => (
                <div
                  className="d-block dropdown-item text-wrap dropdown-item-cart px-3 py-2"
                  key={key}
                >
                  <div className="d-flex align-items-center">
                    <img
                      src={item.image_url || dummyImg}
                      className="me-3 rounded-circle avatar-sm p-2 bg-light"
                      alt="user-pic"
                      onError={(e) => { e.target.onerror = null; e.target.src = dummyImg; }}
                    />
                    <div className="flex-grow-1">
                      <h6 className="mt-0 mb-1 fs-14">{item.company_name}</h6>
                      <p className="mb-0 fs-12 text-muted">
                        Утас: <span>{item.phone}</span>
                        <br />
                        Дуусах:{" "}
                        <span>
                          {moment(item.contract_enddate).format("YYYY-MM-DD")}
                        </span>
                      </p>
                    </div>
                    <div className="px-2 text-end">
                      <h6 className="m-0 fw-normal">
                        <span className="cart-item-price text-danger">
                          {item.daysToExpire < 0
                            ? "Дууссан"
                            : `${item.daysToExpire} хоног үлдсэн`}
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </SimpleBar>

          <div style={{ padding: "12px 16px", borderTop: "1px solid #f0f4f8", background: "#f8fafc" }} id="checkout-elem">
            <Link to="/lgcompanies" className="btn w-100" style={{ background: "linear-gradient(135deg, #f7b84b 0%, #d68910 100%)", color: "#fff", fontWeight: 600, fontSize: 13, borderRadius: 8, padding: "8px 0", border: "none" }}>
              <i className="ri-arrow-right-line me-2"></i>Дэлгэрэнгүй харах
            </Link>
          </div>
        </DropdownMenu>
      </Dropdown>
    </React.Fragment>
  );
};

export default AlertOfExpire;
